import React, { useEffect, useMemo, useState } from 'react';
import { Card } from 'primereact/card';
import { Calendar } from 'primereact/calendar';
import { Dropdown } from 'primereact/dropdown';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TreeTable } from 'primereact/treetable';
import { Column } from 'primereact/column';
import moment from 'moment';
import { getBranches, getUserWaiterSalesHalfDayReport, formatDateForApi } from '../../utilities/api';
import { buildWaiterSessionTree, buildWaiterChildren, WAITER_LEVEL_COLORS, WAITER_LEVEL_ICONS } from '../../utilities/sessionSalesTree';
import useUser from '../../hooks/useUser';
import './billreports.css';

const ALL_BRANCHES_OPTION = { branch_id: 'All', branch_name: 'All Branches' };

const formatCurrency = (amount) => {
    const num = parseFloat(amount || 0);
    return num.toLocaleString('en-IN', { maximumFractionDigits: 2, minimumFractionDigits: 2 });
};

// Backend sends dates as DD-MM-YYYY (occasionally YYYY-MM-DD) — parse explicitly with those
// formats rather than moment's ambiguous auto-parse, which misreads "11-09-2026" as
// MM-DD-YYYY and swaps day/month.
const formatDate = (dateString) => {
    if (!dateString) return '';
    const parsed = moment(dateString, ['DD-MM-YYYY', 'YYYY-MM-DD'], true);
    return parsed.isValid() ? parsed.format('DD-MM-YYYY') : dateString;
};

/**
 * Standalone Session Sales page — Day -> [Branch ->] User -> Waiter -> Payment Mode tree, split
 * into First Half (06:00-11:59) / Second Half (12:00-23:59) / Total. Previously a tab inside
 * Sales Reports; pulled out into its own page (own filters, own Apply) so it works the same for
 * every role instead of depending on that page's shared filter state.
 */
const SessionSalesContent = () => {
    const { userRole } = useUser();
    const rolename = (userRole || localStorage.getItem('role') || '').toUpperCase();
    const isManager = rolename === 'MANAGER';
    const isAdmin = rolename === 'ADMIN';
    const isOperator = rolename === 'OPERATOR';
    // CEO keeps manual From/To pickers + Apply. Manager, Admin and Operator all get an
    // auto-fetched, fixed date range with no pickers — Manager/Admin see today only, Operator
    // sees today + yesterday. Manager's branch is locked to their own; Admin/Operator get an
    // interactive Branch filter.
    const hasDateFilters = !isManager && !isAdmin && !isOperator;
    const autoFetchRole = isManager || isAdmin || isOperator;

    const [fromDate, setFromDate] = useState(() => {
        if (isOperator) {
            const yesterday = new Date();
            yesterday.setDate(yesterday.getDate() - 1);
            return yesterday;
        }
        return new Date();
    });
    const [toDate, setToDate] = useState(new Date());
    const [branches, setBranches] = useState([]);
    const [branchesLoading, setBranchesLoading] = useState(false);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [waiterData, setWaiterData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [expandedKeys, setExpandedKeys] = useState({});

    // Branch list — Manager is locked to their own branch, Admin/CEO can pick or see All.
    useEffect(() => {
        if (isManager) {
            const branchId = localStorage.getItem('branchid') || '';
            const branchName = localStorage.getItem('branchname') || branchId;
            const managerBranch = { branch_id: branchId, branch_name: branchName };
            setBranches([managerBranch]);
            setSelectedBranch(managerBranch);
            return;
        }
        const clientId = localStorage.getItem('clientid');
        if (!clientId) return;
        setBranchesLoading(true);
        getBranches(clientId)
            .then((list) => {
                const withAll = [ALL_BRANCHES_OPTION, ...(Array.isArray(list) ? list : [])];
                setBranches(withAll);
                setSelectedBranch(withAll[0]);
            })
            .catch(() => {
                setBranches([ALL_BRANCHES_OPTION]);
                setSelectedBranch(ALL_BRANCHES_OPTION);
            })
            .finally(() => setBranchesLoading(false));
    }, [isManager]);

    const buildPayload = () => {
        const branchId = selectedBranch?.branch_id || 'All';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: branchId
        };
        if (String(branchId).toLowerCase() === 'all') {
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
        }
        return payload;
    };

    const fetchReport = async () => {
        if (!fromDate || !toDate) return;
        setLoading(true);
        try {
            const res = await getUserWaiterSalesHalfDayReport(buildPayload());
            setWaiterData(res?.status === 'success' && Array.isArray(res?.data) ? res.data : []);
        } catch (error) {
            console.error('Error loading Session Sales report:', error);
            setWaiterData([]);
        } finally {
            setLoading(false);
        }
    };

    // Manager/Admin/Operator have no Apply button — auto-fetch once the branch resolves (and
    // again if Admin/Operator changes branch). CEO gets dates defaulted to today but must click
    // Apply themselves before any data is fetched.
    useEffect(() => {
        if (autoFetchRole && selectedBranch && fromDate && toDate) fetchReport();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [autoFetchRole, selectedBranch]);

    const waiterTreeNodes = useMemo(() => buildWaiterSessionTree(Array.isArray(waiterData) ? waiterData : [], buildWaiterChildren, formatDate), [waiterData]);

    // Date rows start collapsed — only the date parent shows by default, and the user expands
    // a date manually (via the row's expander arrow) to drill into Branch/User/Waiter. Manager
    // and Admin only ever see today's (usually small) tree, so it's expanded fully by default.
    // Operator sees today + yesterday (dates sorted newest-first, so waiterTreeNodes[0] is
    // today) — auto-expand only today's details and leave yesterday's collapsed.
    useEffect(() => {
        if (!autoFetchRole) return;
        if (!waiterTreeNodes.length) return;
        const keys = {};
        const expandSubtree = (node) => {
            if (node.children && node.children.length) {
                keys[node.key] = true;
                node.children.forEach(expandSubtree);
            }
        };
        if (isOperator) {
            expandSubtree(waiterTreeNodes[0]);
        } else {
            waiterTreeNodes.forEach(expandSubtree);
        }
        setExpandedKeys(keys);
    }, [autoFetchRole, isOperator, waiterTreeNodes]);

    const totalBranches = useMemo(() => new Set((waiterData || []).map((r) => r.branch_id ?? '')).size, [waiterData]);
    const totalUsers = useMemo(() => new Set((waiterData || []).map((r) => r.user_id)).size, [waiterData]);
    const totalWaiters = useMemo(() => new Set((waiterData || []).map((r) => r.waiter_no)).size, [waiterData]);

    const labelBody = (node) => {
        const { label, level } = node.data;
        return (
            <span className={`sbw-tree-label sbw-tree-label-${level}`}>
                <i className={`pi ${WAITER_LEVEL_ICONS[level]} mr-2`} style={{ color: WAITER_LEVEL_COLORS[level] }} />
                {label || 'Unassigned'}
            </span>
        );
    };

    const amountBody = (field) => (node) => {
        const half = node.data[field];
        if (!half) return null;
        return <span className={`sbw-tree-amount sbw-tree-amount-${node.data.level}`}>₹{formatCurrency(half.total_sales)}</span>;
    };

    const rowClassName = (node) => ({ [`sbw-tree-row-${node.data.level}`]: true });

    return (
        <div className="bill-reports-container">
            <div className="mb-4">
                <h2 className="text-3xl font-bold mb-2" style={{ color: 'var(--primary-color)' }}>
                    Session Sales
                </h2>
                <p className="text-color-secondary">
                    Day / Branch / Cashier / Waiter / Payment Mode, split into First Half (06:00-11:59) / Second Half (12:00-23:59) / Total.
                </p>
            </div>

            {!isManager && (
                <Card className="mb-4 bill-reports-filter-card">
                    <div className="flex flex-wrap align-items-end bill-reports-filter-row" style={{ gap: '1rem' }}>
                        {hasDateFilters && (
                            <>
                                <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                    <label htmlFor="ss-from">
                                        From Date <span className="text-red-500">*</span>
                                    </label>
                                    <Calendar id="ss-from" value={fromDate} onChange={(e) => setFromDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="From Date" className="w-full" showButtonBar />
                                </div>
                                <div className="flex flex-column" style={{ flex: '1 1 150px', minWidth: '140px' }}>
                                    <label htmlFor="ss-to">
                                        To Date <span className="text-red-500">*</span>
                                    </label>
                                    <Calendar id="ss-to" value={toDate} onChange={(e) => setToDate(e.value)} dateFormat="dd-mm-yy" showIcon placeholder="To Date" minDate={fromDate} className="w-full" showButtonBar />
                                </div>
                            </>
                        )}
                        <div className="flex flex-column" style={{ flex: '1 1 200px', minWidth: '180px' }}>
                            <label htmlFor="ss-branch">Branch</label>
                            <Dropdown
                                id="ss-branch"
                                value={selectedBranch}
                                options={branches}
                                onChange={(e) => setSelectedBranch(e.value)}
                                optionLabel="branch_name"
                                className="w-full"
                                loading={branchesLoading || undefined}
                            />
                        </div>
                        {hasDateFilters && (
                            <div className="flex gap-2" style={{ flex: '0 0 auto' }}>
                                <Button label="Apply" icon="pi pi-check" className="p-button-primary" onClick={fetchReport} disabled={!fromDate || !toDate || !selectedBranch || loading} loading={loading} />
                            </div>
                        )}
                    </div>
                </Card>
            )}

            <Card className="mb-4 sbw-tree-card">
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Session Sales</h3>
                    <div className="flex gap-2">
                        {totalBranches > 1 && <Tag value={`${totalBranches} Branches`} style={{ background: WAITER_LEVEL_COLORS.branch }} />}
                        {totalUsers > 0 && <Tag value={`${totalUsers} User${totalUsers === 1 ? '' : 's'}`} style={{ background: WAITER_LEVEL_COLORS.user }} />}
                        {totalWaiters > 0 && <Tag value={`${totalWaiters} Waiter${totalWaiters === 1 ? '' : 's'}`} style={{ background: WAITER_LEVEL_COLORS.waiter }} />}
                    </div>
                </div>
                {loading ? (
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '200px' }}>
                        <ProgressSpinner />
                    </div>
                ) : waiterTreeNodes.length === 0 ? (
                    <div className="flex flex-column align-items-center justify-content-center p-5">
                        <i className="pi pi-users text-5xl text-color-secondary mb-3"></i>
                        <p className="text-color-secondary">{waiterData === null ? 'Select a date range and branch, then Apply.' : 'No waiter sales found for the selected filters.'}</p>
                    </div>
                ) : (
                    <TreeTable
                        value={waiterTreeNodes}
                        expandedKeys={expandedKeys}
                        onToggle={(e) => setExpandedKeys(e.value)}
                        rowClassName={rowClassName}
                        className="p-treetable-sm sbw-tree"
                        tableStyle={{ minWidth: '45rem' }}
                    >
                        <Column field="label" header="Day / Branch / User / Waiter / Payment Mode" body={labelBody} expander style={{ minWidth: '260px' }} />
                        <Column field="first_half" header="First Half (06:00-11:59)" body={amountBody('first_half')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '170px' }} />
                        <Column field="second_half" header="Second Half (12:00-23:59)" body={amountBody('second_half')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '170px' }} />
                        <Column field="total" header="Total" body={amountBody('total')} style={{ textAlign: 'right', justifyContent: 'flex-end', minWidth: '150px' }} />
                    </TreeTable>
                )}
            </Card>
        </div>
    );
};

export default SessionSalesContent;
